import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import { useTheme, useMediaQuery } from '@mui/material'

import FolderIcon from '@mui/icons-material/Folder'
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import EditIcon from '@mui/icons-material/Edit'
import ShareIcon from '@mui/icons-material/Share'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import ImageIcon from '@mui/icons-material/Image'
import TableChartIcon from '@mui/icons-material/TableChart'
import DescriptionIcon from '@mui/icons-material/Description'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'

import urls from '../../../utils/urls'
import {
  makeGetRequest,
  makeGetAllRequest,
  makePostRequest,
  makePatchRequest,
  makeDeleteRequest,
} from '../../../utils/api'
import { convertToUserDateFormate } from 'utils/helpers'
import { useSelector } from 'react-redux'
import CreateOperationForm from '../CreateOperationForm'
import EditOperationForm from '../EditOperationForm'
import CreateMessage from 'containers/MyInbox/CreateMessage'
import FolderPickerDialog from './FolderPickerDialog'

// Type MIME "maison" du drag & drop interne de l'explorateur : il transporte
// la sélection (documents + dossiers) déposée sur un dossier ou sur le fil
// d'Ariane.
const DND_TYPE = 'application/x-life-documents'

function fileIcon(doc) {
  const name = String(doc.Lien || doc.Nom || '').toLowerCase()
  if (name.endsWith('.pdf')) return <PictureAsPdfIcon sx={{ color: '#d93025' }} />
  if (/\.(png|jpe?g|gif|webp|svg|heic)$/.test(name)) return <ImageIcon sx={{ color: '#188038' }} />
  if (/\.(xlsx?|csv|ods)$/.test(name)) return <TableChartIcon sx={{ color: '#188038' }} />
  if (/\.(docx?|odt|txt|md)$/.test(name)) return <DescriptionIcon sx={{ color: '#1a73e8' }} />
  return <InsertDriveFileIcon sx={{ color: '#5f6368' }} />
}

export default function DocumentsExplorer() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const location = useLocation()
  const siteUser = useSelector((store) => store.user)

  const currentFolderId = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('folder')
    const id = parseInt(raw, 10)
    return Number.isFinite(id) ? id : null
  }, [location.search])

  const [folders, setFolders] = useState([])
  // même forme que la vue liste ({results, count}) pour réutiliser tels quels
  // CreateOperationForm / EditOperationForm
  const [operations, setOperations] = useState({ results: [], count: 0 })
  const [loading, setLoading] = useState(true)

  const [selDocs, setSelDocs] = useState([])
  const [selFolders, setSelFolders] = useState([])

  // menu contextuel / menu ⋮ : { position | anchorEl, item: {kind, data} | null }
  const [menu, setMenu] = useState(null)
  const [newFolderOpen, setNewFolderOpen] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [renameTarget, setRenameTarget] = useState(null)
  const [renameValue, setRenameValue] = useState('')
  const [picker, setPicker] = useState(null) // { action: 'move' | 'copy' }
  const [confirmDel, setConfirmDel] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [dragOverId, setDragOverId] = useState(null) // id dossier | 'root'

  const childFolders = useMemo(
    () => folders.filter((f) => (f.parent ?? null) === currentFolderId),
    [folders, currentFolderId],
  )

  // Fil d'Ariane : remonte les parents depuis la liste plate
  const breadcrumb = useMemo(() => {
    const byId = new Map(folders.map((f) => [f.id, f]))
    const path = []
    let cursor = byId.get(currentFolderId)
    while (cursor) {
      path.unshift(cursor)
      cursor = byId.get(cursor.parent)
    }
    return path
  }, [folders, currentFolderId])

  const fetchAll = async () => {
    setLoading(true)
    const [treeResp, docsResp] = await Promise.all([
      makeGetRequest(urls.fetchDocumentsTree, {}, true),
      // PAGE_SIZE=20 côté DRF : makeGetAllRequest suit la pagination pour
      // avoir TOUT le contenu du dossier, pas seulement les 20 premiers.
      makeGetAllRequest(
        `${urls.crudDocumentsDetailsOperations}?folder=${currentFolderId ?? 'root'}`,
        {},
        true,
      ),
    ])
    if (treeResp.status === 200) setFolders(treeResp.data)
    if (docsResp.status === 200) setOperations(docsResp.data)
    setLoading(false)
  }

  useEffect(() => {
    fetchAll()
    setSelDocs([])
    setSelFolders([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolderId])

  const openFolder = (id) => {
    const params = new URLSearchParams(location.search)
    params.set('view', 'explorer')
    if (id == null) params.delete('folder')
    else params.set('folder', id)
    navigate(`?${params.toString()}`)
  }

  // ------------------------------------------------------------- sélection

  const toggleDoc = (id) =>
    setSelDocs((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
  const toggleFolder = (id) =>
    setSelFolders((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
  const clearSelection = () => {
    setSelDocs([])
    setSelFolders([])
  }
  const selectionCount = selDocs.length + selFolders.length

  // La cible d'une action de menu : l'élément cliqué s'il ne fait pas partie
  // de la sélection courante, sinon toute la sélection (comme OneDrive).
  const actionTargets = (item) => {
    if (!item) return { documents: selDocs, folders: selFolders }
    const inSelection =
      item.kind === 'doc' ? selDocs.includes(item.data.id) : selFolders.includes(item.data.id)
    if (inSelection && selectionCount > 0) return { documents: selDocs, folders: selFolders }
    return item.kind === 'doc'
      ? { documents: [item.data.id], folders: [] }
      : { documents: [], folders: [item.data.id] }
  }

  // --------------------------------------------------------------- actions

  const createFolder = async () => {
    const nom = newFolderName.trim()
    if (!nom) return
    const body = { Nom: nom }
    if (currentFolderId != null) body.parent = currentFolderId
    const resp = await makePostRequest(urls.crudDocumentFolders, body, {}, true)
    if (resp.status === 201) {
      setNewFolderOpen(false)
      setNewFolderName('')
      fetchAll()
    }
  }

  const renameFolder = async () => {
    const nom = renameValue.trim()
    if (!nom || !renameTarget) return
    const resp = await makePatchRequest(
      `${urls.crudDocumentFolders}${renameTarget.id}/`,
      { Nom: nom },
      {},
      true,
    )
    if (resp.status === 200) {
      setRenameTarget(null)
      fetchAll()
    }
  }

  const moveOrCopy = async (targets, targetFolderId, action) => {
    const resp = await makePostRequest(
      urls.moveDocumentsItems,
      {
        documents: targets.documents,
        folders: targets.folders,
        target: targetFolderId,
        action,
      },
      {},
      true,
    )
    if (resp.status === 200) {
      clearSelection()
      fetchAll()
    } else if (resp.status === 400 && resp.data?.detail) {
      alert(resp.data.detail)
    }
  }

  const deleteTargets = async (targets) => {
    if (targets.documents.length > 0) {
      await makePostRequest(
        urls.deleteDocumentsBulkOperations,
        { selected: targets.documents },
        {},
        true,
      )
    }
    // Suppression d'un dossier = son contenu remonte au parent (backend),
    // aucun document n'est perdu.
    for (const id of targets.folders) {
      // eslint-disable-next-line no-await-in-loop
      await makeDeleteRequest(`${urls.crudDocumentFolders}${id}/`, {}, true)
    }
    clearSelection()
    fetchAll()
  }

  // ----------------------------------------------------------- drag & drop

  const handleDragStart = (e, item) => {
    const targets = actionTargets(item)
    e.dataTransfer.setData(DND_TYPE, JSON.stringify(targets))
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDropOn = (e, targetFolderId) => {
    e.preventDefault()
    setDragOverId(null)
    let payload
    try {
      payload = JSON.parse(e.dataTransfer.getData(DND_TYPE))
    } catch (err) {
      return
    }
    if (!payload) return
    // pas de dépôt d'un dossier sur lui-même (le backend re-vérifie les cycles)
    const foldersToMove = (payload.folders || []).filter((id) => id !== targetFolderId)
    if ((payload.documents || []).length === 0 && foldersToMove.length === 0) return
    moveOrCopy({ documents: payload.documents || [], folders: foldersToMove }, targetFolderId, 'move')
  }

  const dropZoneProps = (targetFolderId) => ({
    onDragOver: (e) => {
      if (e.dataTransfer.types.includes(DND_TYPE)) {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        setDragOverId(targetFolderId ?? 'root')
      }
    },
    onDragLeave: () => setDragOverId(null),
    onDrop: (e) => handleDropOn(e, targetFolderId),
  })

  // ------------------------------------------------------------------ menu

  const openContextMenu = (e, item) => {
    e.preventDefault()
    e.stopPropagation()
    setMenu({ position: { left: e.clientX + 2, top: e.clientY - 6 }, item })
  }
  const openItemMenu = (e, item) => {
    e.stopPropagation()
    setMenu({ anchorEl: e.currentTarget, item })
  }
  const closeMenu = () => setMenu(null)

  const menuItem = menu?.item
  const menuDoc = menuItem?.kind === 'doc' ? menuItem.data : null
  const menuFolder = menuItem?.kind === 'folder' ? menuItem.data : null

  const singleSelectedDoc =
    selDocs.length === 1 && selFolders.length === 0
      ? operations.results.find((d) => d.id === selDocs[0])
      : null

  // ------------------------------------------------------------------ rendu

  return (
    <Container maxWidth="xl">
      {/* barre d'outils : fil d'Ariane + actions */}
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        py={1.5}
        gap={1}
      >
        <Breadcrumbs sx={{ '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}>
          <Box
            onClick={() => openFolder(null)}
            {...dropZoneProps(null)}
            sx={{
              cursor: 'pointer',
              fontWeight: 600,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              color: '#001b42',
              bgcolor: dragOverId === 'root' ? '#e3f0fb' : 'transparent',
              '&:hover': { bgcolor: '#eef2f6' },
            }}
          >
            Mes Documents
          </Box>
          {breadcrumb.map((f) => (
            <Box
              key={f.id}
              onClick={() => openFolder(f.id)}
              {...dropZoneProps(f.id)}
              sx={{
                cursor: 'pointer',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontWeight: f.id === currentFolderId ? 600 : 400,
                bgcolor: dragOverId === f.id ? '#e3f0fb' : 'transparent',
                '&:hover': { bgcolor: '#eef2f6' },
              }}
            >
              {f.Nom}
            </Box>
          ))}
        </Breadcrumbs>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {selectionCount > 0 ? (
            <>
              <Typography variant="body2" sx={{ color: '#555' }}>
                {selectionCount} sélectionné{selectionCount > 1 ? 's' : ''}
              </Typography>
              <Tooltip title="Déplacer vers…">
                <IconButton size="small" onClick={() => setPicker({ action: 'move' })}>
                  <DriveFileMoveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copier vers…">
                <IconButton size="small" onClick={() => setPicker({ action: 'copy' })}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
              {singleSelectedDoc && (
                <>
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => setEditOpen(true)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Partager">
                    <IconButton size="small" onClick={() => setShareOpen(true)}>
                      <ShareIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <Tooltip title="Supprimer">
                <IconButton size="small" color="error" onClick={() => setConfirmDel(true)}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
              <Button size="small" onClick={clearSelection}>
                Annuler
              </Button>
            </>
          ) : (
            <>
              <Button
                size="small"
                startIcon={<CreateNewFolderIcon />}
                onClick={() => setNewFolderOpen(true)}
                sx={{ color: '#001b42', textTransform: 'none', fontWeight: 600 }}
              >
                Nouveau dossier
              </Button>
              <CreateOperationForm setOperations={setOperations} folder={currentFolderId} />
            </>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{ background: 'white', borderRadius: '5px', p: 2, minHeight: 400 }}
          onContextMenu={(e) => {
            // clic droit sur le fond : menu du dossier courant
            if (e.target === e.currentTarget) openContextMenu(e, null)
          }}
        >
          {/* ----------------------------------------------------- dossiers */}
          {childFolders.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
                Dossiers
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1.5} mb={3}>
                {childFolders.map((folder) => {
                  const selected = selFolders.includes(folder.id)
                  return (
                    <Box
                      key={folder.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, { kind: 'folder', data: folder })}
                      {...dropZoneProps(folder.id)}
                      onClick={() => openFolder(folder.id)}
                      onContextMenu={(e) => openContextMenu(e, { kind: 'folder', data: folder })}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: isMobile ? '100%' : 230,
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: selected ? '#4a92d3' : '#e0e0e0',
                        bgcolor:
                          dragOverId === folder.id ? '#e3f0fb' : selected ? '#f0f7fd' : '#fafbfc',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': { boxShadow: 1, '& .tile-actions': { opacity: 1 } },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={selected}
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleFolder(folder.id)
                        }}
                        sx={{ p: 0.25 }}
                      />
                      <FolderIcon sx={{ color: '#f8ce46', fontSize: 32 }} />
                      <Box flex={1} minWidth={0}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          title={folder.Nom}
                        >
                          {folder.Nom}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>
                          {folder.children_count} dossier{folder.children_count > 1 ? 's' : ''} ·{' '}
                          {folder.documents_count} doc{folder.documents_count > 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        className="tile-actions"
                        sx={{ opacity: isMobile ? 1 : 0 }}
                        onClick={(e) => openItemMenu(e, { kind: 'folder', data: folder })}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )
                })}
              </Box>
            </>
          )}

          {/* ---------------------------------------------------- documents */}
          <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
            Documents ({operations.results.length})
          </Typography>
          {operations.results.length === 0 && childFolders.length === 0 && (
            <Box py={5} textAlign="center" sx={{ color: '#999' }}>
              Ce dossier est vide. Créez un dossier ou ajoutez un document, ou
              glissez-déposez des éléments ici depuis un autre dossier.
            </Box>
          )}
          {operations.results.map((doc) => {
            const selected = selDocs.includes(doc.id)
            return (
              <Box
                key={doc.id}
                draggable
                onDragStart={(e) => handleDragStart(e, { kind: 'doc', data: doc })}
                onContextMenu={(e) => openContextMenu(e, { kind: 'doc', data: doc })}
                onClick={() => toggleDoc(doc.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1,
                  py: 0.75,
                  borderRadius: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  bgcolor: selected ? '#f0f7fd' : 'transparent',
                  '&:hover': { bgcolor: selected ? '#f0f7fd' : '#f6f8fa', '& .row-actions': { opacity: 1 } },
                }}
              >
                <Checkbox
                  size="small"
                  checked={selected}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleDoc(doc.id)
                  }}
                  sx={{ p: 0.25 }}
                />
                {fileIcon(doc)}
                <Box flex={1} minWidth={0}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    title={doc.Nom}
                    onClick={(e) => {
                      e.stopPropagation()
                      window.open(doc.Lien)
                    }}
                    sx={{ '&:hover': { textDecoration: 'underline', color: '#1a73e8' } }}
                  >
                    {doc.Nom}
                  </Typography>
                  {isMobile && (
                    <Typography variant="caption" sx={{ color: '#888' }} noWrap>
                      {doc.Types?.Type}
                    </Typography>
                  )}
                </Box>
                {!isMobile && (
                  <>
                    <Typography variant="body2" sx={{ width: 160, color: '#555' }} noWrap>
                      {doc.Types?.Type}
                    </Typography>
                    <Typography variant="body2" sx={{ width: 110, color: '#555' }}>
                      {doc.Datedéchéance
                        ? convertToUserDateFormate(siteUser.user, doc.Datedéchéance)
                        : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ width: 80, color: '#555' }}>
                      {doc.FileSize != null ? `${doc.FileSize} Mo` : ''}
                    </Typography>
                  </>
                )}
                <IconButton
                  size="small"
                  className="row-actions"
                  sx={{ opacity: isMobile ? 1 : 0 }}
                  onClick={(e) => openItemMenu(e, { kind: 'doc', data: doc })}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            )
          })}
        </Box>
      )}

      {/* --------------------------------------------------- menu contextuel */}
      <Menu
        open={Boolean(menu)}
        onClose={closeMenu}
        anchorEl={menu?.anchorEl}
        anchorReference={menu?.position ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={menu?.position}
      >
        {menuDoc && (
          <MenuItem
            onClick={() => {
              window.open(menuDoc.Lien)
              closeMenu()
            }}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ouvrir</ListItemText>
          </MenuItem>
        )}
        {menuDoc && (
          <MenuItem
            onClick={() => {
              window.open(menuDoc.Lien)
              closeMenu()
            }}
          >
            <ListItemIcon>
              <FileDownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Télécharger</ListItemText>
          </MenuItem>
        )}
        {menuDoc && (
          <MenuItem
            onClick={() => {
              setSelDocs([menuDoc.id])
              setSelFolders([])
              setEditOpen(true)
              closeMenu()
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Modifier</ListItemText>
          </MenuItem>
        )}
        {menuDoc && (
          <MenuItem
            onClick={() => {
              setSelDocs([menuDoc.id])
              setSelFolders([])
              setShareOpen(true)
              closeMenu()
            }}
          >
            <ListItemIcon>
              <ShareIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Partager</ListItemText>
          </MenuItem>
        )}
        {menuFolder && (
          <MenuItem
            onClick={() => {
              openFolder(menuFolder.id)
              closeMenu()
            }}
          >
            <ListItemIcon>
              <FolderIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ouvrir</ListItemText>
          </MenuItem>
        )}
        {menuFolder && (
          <MenuItem
            onClick={() => {
              setRenameTarget(menuFolder)
              setRenameValue(menuFolder.Nom)
              closeMenu()
            }}
          >
            <ListItemIcon>
              <DriveFileRenameOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Renommer</ListItemText>
          </MenuItem>
        )}
        {menuItem && <Divider />}
        {menuItem && (
          <MenuItem
            onClick={() => {
              setPicker({ action: 'move', item: menuItem })
              closeMenu()
            }}
          >
            <ListItemIcon>
              <DriveFileMoveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Déplacer vers…</ListItemText>
          </MenuItem>
        )}
        {menuItem && (
          <MenuItem
            onClick={() => {
              setPicker({ action: 'copy', item: menuItem })
              closeMenu()
            }}
          >
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copier vers…</ListItemText>
          </MenuItem>
        )}
        {menuItem && (
          <MenuItem
            onClick={() => {
              const targets = actionTargets(menuItem)
              setSelDocs(targets.documents)
              setSelFolders(targets.folders)
              setConfirmDel(true)
              closeMenu()
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Supprimer</ListItemText>
          </MenuItem>
        )}
        {!menuItem && (
          <MenuItem
            onClick={() => {
              setNewFolderOpen(true)
              closeMenu()
            }}
          >
            <ListItemIcon>
              <CreateNewFolderIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Nouveau dossier</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* ----------------------------------------------------------- dialogues */}
      <Dialog fullWidth maxWidth="xs" open={newFolderOpen} onClose={() => setNewFolderOpen(false)}>
        <DialogTitle sx={{ color: 'black !important' }}>Nouveau dossier</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            margin="dense"
            label="Nom du dossier"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && createFolder()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={createFolder}>Créer</Button>
          <Button onClick={() => setNewFolderOpen(false)}>Annuler</Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="xs" open={Boolean(renameTarget)} onClose={() => setRenameTarget(null)}>
        <DialogTitle sx={{ color: 'black !important' }}>Renommer le dossier</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            margin="dense"
            label="Nom du dossier"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && renameFolder()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={renameFolder}>Renommer</Button>
          <Button onClick={() => setRenameTarget(null)}>Annuler</Button>
        </DialogActions>
      </Dialog>

      {picker && (
        <FolderPickerDialog
          folders={folders}
          excludeIds={actionTargets(picker.item).folders}
          title={picker.action === 'copy' ? 'Copier vers…' : 'Déplacer vers…'}
          confirmLabel={picker.action === 'copy' ? 'Copier ici' : 'Déplacer ici'}
          onConfirm={(target) => {
            moveOrCopy(actionTargets(picker.item), target, picker.action)
            setPicker(null)
          }}
          onClose={() => setPicker(null)}
        />
      )}

      <Dialog open={confirmDel} onClose={() => setConfirmDel(false)}>
        <DialogContent>
          <DialogContentText>
            {selFolders.length > 0
              ? 'Supprimer la sélection ? Le contenu des dossiers supprimés (sous-dossiers et documents) sera déplacé vers le dossier parent — les documents supprimés, eux, le sont définitivement.'
              : 'êtes-vous sur de vouloir supprimer ce document ? (cette opération est irréversible !)'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDel(false)
              deleteTargets({ documents: selDocs, folders: selFolders })
            }}
          >
            Oui
          </Button>
          <Button onClick={() => setConfirmDel(false)}>Non</Button>
        </DialogActions>
      </Dialog>

      {editOpen && singleSelectedDoc && (
        <EditOperationForm
          setOperations={setOperations}
          setSelected={(v) => {
            setEditOpen(Boolean(v))
            if (!v) fetchAll()
          }}
          params={{ 0: singleSelectedDoc }}
          operations={operations}
        />
      )}
      {shareOpen && singleSelectedDoc && (
        <CreateMessage
          setMOpen={setShareOpen}
          title={'Partage document'}
          settingId={2}
          document={singleSelectedDoc}
        />
      )}
    </Container>
  )
}
